/**
 * O modelo de valores da tela — um `FormGroup` por instância de tela.
 *
 * Este é o pré-requisito estrutural da seção 3.1 do plano, e a razão dele existir
 * merece ser dita por inteiro, porque explica metade dos bugs do módulo antigo.
 *
 * Hoje o canvas tem `dummyFormGroup = this.fb.group({})` — um formulário VAZIO,
 * com o comentário "para evitar erros de FormControl em campos ng-zorro". Como os
 * controles do NG-Zorro não acham o controle correspondente, ficam `ng-invalid`;
 * como ficam inválidos, o Ant os pinta de vermelho; como ficam vermelhos, o
 * canvas apaga as classes na mão e força
 * `element.style.setProperty('color', 'rgba(0,0,0,0.85)', 'important')`; e como o
 * Angular reaplica as classes, há um `MutationObserver` — sem `disconnect()` —
 * para forçar de novo. Nos temas escuros o texto fica preto sobre fundo preto e
 * nenhum CSS resolve, porque a cor está inline com `!important`.
 *
 * Nada disso é reimplementado aqui. Com um formulário de verdade, o controle
 * existe, não fica inválido, o Ant não pinta nada, e não há o que combater.
 *
 * Além da cor, o formulário destrava o que hoje é `console.log`: `setValue`,
 * `clearValue`, condições sobre outros campos, `onChange` de fato disparado,
 * `getAllFormData()` devolvendo dados, e a prévia com dados reais.
 */
import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators,
  FormArray
} from '@angular/forms';

import { NodeType, ScreenNode } from '../model/node-props';
import { ScreenDocument } from '../model/screen-schema';
import { ScreenComponentRegistry } from '../registry/component-registry';
import { ResolvedValue } from './rule-evaluator';

/**
 * Profundidade máxima de reações em cadeia.
 *
 * Um `onChange` de A pode escrever em B, cujo `onChange` escreve em C. Se C
 * escrever de volta em A, o ciclo é infinito. O limite corta a cadeia e reporta,
 * em vez de travar a aba do usuário.
 */
export const MAX_CASCADE_DEPTH = 10;

/** O formulário mais os índices que ligam nó, caminho e controle. */
export interface ScreenForm {
  readonly group: FormGroup;

  /** Nome do controle a partir do id do nó. */
  readonly controlByNodeId: ReadonlyMap<string, string>;
  /** Id do nó a partir do nome do controle — o caminho de volta. */
  readonly nodeIdByControl: ReadonlyMap<string, string>;

  /**
   * Nomes de controle alcançáveis por um caminho de regra.
   *
   * Uma regra pode se referir a um campo de três formas, e todas precisam
   * funcionar: pelo `binding.fieldName` (o nome da coluna), pelo
   * `binding.dataPath` (o caminho no JSON) ou pelo id do nó. Um caminho pode
   * casar com mais de um campo — dois componentes lendo o mesmo `dataPath` é
   * comum e legítimo.
   */
  readonly controlsByPath: ReadonlyMap<string, readonly string[]>;
}

/** O que a montagem do formulário encontrou de errado. */
export interface FormBuildIssue {
  readonly kind: 'duplicateControl' | 'unknownType' | 'cascadeTooDeep';
  readonly nodeId: string;
  readonly detail: string;
}

@Injectable()
export class ScreenFormService {

  constructor(private readonly registry: ScreenComponentRegistry) { }

  /**
   * Monta o formulário da tela inteira.
   *
   * Plano, não aninhado. Aninhar por painel pareceria mais organizado, mas
   * amarraria o nome do controle à posição do campo na árvore — e aí mover um
   * campo de painel no editor renomearia o controle, quebrando toda regra que
   * apontasse para ele. O nome vem do nó, que não muda quando o campo se move.
   */
  buildForm(
    document: ScreenDocument,
    issues?: FormBuildIssue[]
  ): ScreenForm {

    const controls: Record<string, AbstractControl> = {};
    const controlByNodeId = new Map<string, string>();
    const nodeIdByControl = new Map<string, string>();
    const controlsByPath = new Map<string, string[]>();

    const visit = (node: ScreenNode): void => {
      this.addControlForNode(node, controls, controlByNodeId, nodeIdByControl, controlsByPath, issues);

      if (node.children !== undefined) {
        for (const child of node.children) {
          visit(child);
        }
      }
    };

    visit(document.root);

    return {
      group: new FormGroup(controls),
      controlByNodeId,
      nodeIdByControl,
      controlsByPath
    };
  }

  private addControlForNode(
    node: ScreenNode,
    controls: Record<string, AbstractControl>,
    controlByNodeId: Map<string, string>,
    nodeIdByControl: Map<string, string>,
    controlsByPath: Map<string, string[]>,
    issues?: FormBuildIssue[]
  ): void {

    if (!this.registry.has(node.type)) {
      this.report(issues, 'unknownType', node.id, 'tipo sem descriptor: ' + node.type);
      return;
    }

    const descriptor = this.registry.get(node.type);
    if (descriptor.form === undefined) {
      // Container ou componente estático: não guarda valor.
      return;
    }

    const controlName = node.id;

    if (controls[controlName] !== undefined) {
      // Id duplicado na árvore. O migrador já normaliza, mas o editor de JSON
      // aceita o que o usuário colar — melhor reportar que sobrescrever.
      this.report(issues, 'duplicateControl', node.id, 'já existe controle com este id');
      return;
    }

    const initialValue = descriptor.form.defaultValue(node as never);
    const validators = this.buildValidators(node, descriptor.form.isRequired);
    // A FORMA do controle vem do descriptor. Tabela e tabela simples precisam
    // de `FormArray` porque o widget delas faz `[formArrayName]` — com um
    // `FormControl` ali, o Angular estoura e a tela inteira nao renderiza.
    const control: AbstractControl = descriptor.form?.control === 'array'
      ? new FormArray<AbstractControl>(
          (Array.isArray(initialValue) ? initialValue : []).map(
            item => new FormControl(item)
          ),
          { validators }
        )
      : new FormControl(initialValue, { validators });

    if (this.isReadonly(node)) {
      control.disable({ emitEvent: false });
    }

    controls[controlName] = control;
    controlByNodeId.set(node.id, controlName);
    nodeIdByControl.set(controlName, node.id);

    this.indexPath(controlsByPath, node.id, controlName);
    if (node.binding?.fieldName !== undefined) {
      this.indexPath(controlsByPath, node.binding.fieldName, controlName);
    }
    if (node.binding?.dataPath !== undefined) {
      this.indexPath(controlsByPath, node.binding.dataPath, controlName);
    }
  }

  /**
   * Só a obrigatoriedade ESTÁTICA entra como validador do Angular.
   *
   * A condicional (`requiredWhen`) fica fora de propósito: ela muda conforme os
   * valores da tela, e reconstruir validador a cada mudança é caro e sujeito a
   * ficar dessincronizado. Quem aplica `requiredWhen` é o runtime de regras, que
   * chama `setRequired` sobre o controle quando a condição vira.
   */
  private buildValidators(
    node: ScreenNode,
    isRequired: ((node: ScreenNode<NodeType>) => boolean) | undefined
  ): ValidatorFn[] {

    const validators: ValidatorFn[] = [];

    if (isRequired !== undefined && isRequired(node)) {
      validators.push(Validators.required);
    }

    return validators;
  }

  private isReadonly(node: ScreenNode): boolean {
    const props = node.props as { readonly?: boolean };
    return props.readonly === true;
  }

  private indexPath(
    controlsByPath: Map<string, string[]>,
    path: string,
    controlName: string
  ): void {

    if (path.length === 0) {
      return;
    }

    const existing = controlsByPath.get(path);
    if (existing === undefined) {
      controlsByPath.set(path, [controlName]);
      return;
    }
    if (existing.indexOf(controlName) < 0) {
      existing.push(controlName);
    }
  }

  private report(
    issues: FormBuildIssue[] | undefined,
    kind: FormBuildIssue['kind'],
    nodeId: string,
    detail: string
  ): void {

    if (issues !== undefined) {
      issues.push({ kind, nodeId, detail });
    }
  }

  // ---------------------------------------------------------------- leitura

  /**
   * Resolvedor para o avaliador de regras.
   *
   * Devolve `found: false` quando o caminho não casa com nenhum controle — é
   * assim que a referência órfã chega ao relatório em vez de virar `undefined`
   * silencioso.
   */
  createResolver(screenForm: ScreenForm): (path: string) => ResolvedValue {
    return (path: string): ResolvedValue => {
      const controlNames = screenForm.controlsByPath.get(path);

      if (controlNames === undefined || controlNames.length === 0) {
        return { found: false, value: undefined };
      }

      const control = screenForm.group.get(controlNames[0]);
      if (control === null) {
        return { found: false, value: undefined };
      }

      // `.value` de controle desabilitado é o valor guardado, que é o que a
      // regra quer saber — campo somente-leitura ainda tem conteúdo.
      return { found: true, value: control.value };
    };
  }

  /**
   * Todos os valores da tela, por nome de campo quando existe.
   *
   * É o `getAllFormData()` que hoje devolve `{}` com o comentário "estamos em
   * contexto síncrono".
   */
  collectValues(screenForm: ScreenForm, document: ScreenDocument): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    const nodeById = new Map<string, ScreenNode>();

    const visit = (node: ScreenNode): void => {
      nodeById.set(node.id, node);
      if (node.children !== undefined) {
        for (const child of node.children) {
          visit(child);
        }
      }
    };
    visit(document.root);

    for (const [controlName, nodeId] of screenForm.nodeIdByControl) {
      const control = screenForm.group.get(controlName);
      if (control === null) {
        continue;
      }

      const node = nodeById.get(nodeId);
      let key: string;

      if (node?.binding?.fieldName !== undefined && node.binding.fieldName.length > 0) {
        key = node.binding.fieldName;
      } else {
        key = nodeId;
      }

      values[key] = control.value;
    }

    return values;
  }

  // ---------------------------------------------------------------- escrita

  /**
   * Escreve num controle SEM disparar `valueChanges`.
   *
   * Toda escrita programática passa por aqui. É a guarda combinada na seção 3.1:
   * sem `emitEvent: false`, uma ação `setValue` dispararia o `onChange` do campo
   * escrito, que dispararia outra ação, e a tela entraria em laço. Quem precisa
   * encadear reações faz isso explicitamente, pelo runtime de eventos, que conta
   * a profundidade.
   */
  writeValue(screenForm: ScreenForm, nodeId: string, value: unknown): boolean {
    const controlName = screenForm.controlByNodeId.get(nodeId);
    if (controlName === undefined) {
      return false;
    }

    const control = screenForm.group.get(controlName);
    if (control === null) {
      return false;
    }

    control.setValue(value, { emitEvent: false });
    control.markAsDirty();
    return true;
  }

  clearValue(screenForm: ScreenForm, nodeId: string): boolean {
    return this.writeValue(screenForm, nodeId, null);
  }

  setRequired(screenForm: ScreenForm, nodeId: string, required: boolean): boolean {
    const controlName = screenForm.controlByNodeId.get(nodeId);
    if (controlName === undefined) {
      return false;
    }

    const control = screenForm.group.get(controlName);
    if (control === null) {
      return false;
    }

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
    return true;
  }

  setReadonly(screenForm: ScreenForm, nodeId: string, readonly: boolean): boolean {
    const controlName = screenForm.controlByNodeId.get(nodeId);
    if (controlName === undefined) {
      return false;
    }

    const control = screenForm.group.get(controlName);
    if (control === null) {
      return false;
    }

    if (readonly) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
    return true;
  }
}
